import { AnchorHTMLAttributes, DetailedHTMLProps, ButtonHTMLAttributes, forwardRef } from "react";
import { Link, LinkProps as ReactRouterLinkProps } from "react-router-dom";
import cx from "classnames";
import { Icon } from "./Icon";
import { IconName } from "./Icons";

type ButtonOrLinkProps = {
  kind:
    | "primary-teal"
    | "primary-dark"
    | "primary-purple"
    | "primary-blue"
    | "secondary-teal"
    | "secondary-purple"
    | "secondary-dark"
    | "tertiary"
    | "warning"
    | "warning-new"
    | "secondary-gray"
    | "close-icon";
  size?: "normal" | "small" | "medium" | "large";
  icon?: IconName;
  disabled?: boolean;
};

type ButtonProps = DetailedHTMLProps<
  ButtonHTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
> &
  ButtonOrLinkProps;

type LinkProps = ReactRouterLinkProps & ButtonOrLinkProps;

type ExternalLinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & ButtonOrLinkProps;

const getKindClassName = (kind: ButtonOrLinkProps["kind"], disabled: boolean) => {
  switch (kind) {
    case "primary-teal": {
      return [
        "bg-teal-300 text-white hover:bg-teal-medium border-teal-muted-dark",
        disabled &&
          "bg-disabled-teal-300 border-disabled-teal-300 hover:bg-disabled-teal-300 hover:border-disabled-teal-300"
      ];
    }
    case "primary-dark": {
      return [
        "bg-teal-medium text-white border-teal-medium",
        disabled && "opacity-50"
      ];
    }
    case "primary-purple": {
      return [
        !disabled &&
          "bg-indigo-500 border-indigo-500 text-white hover:bg-indigo-600 hover:border-indigo-600",
        disabled && "bg-indigo-200 border-indigo-200 text-indigo-100"
      ];
    }
    case "primary-blue": {
      return [
        "bg-blue-300 text-white border-blue-300",
        !disabled && "hover:bg-blue-400 hover:border-blue-400",
        disabled && "opacity-50"
      ];
    }
    case "secondary-teal": {
      return [
        "bg-white text-teal-300 border-teal-300",
        !disabled && "hover:text-teal-medium hover:border-teal-medium"
      ];
    }
    case "secondary-purple": {
      return [
        !disabled &&
          "bg-indigo-100 border-indigo-500 text-indigo-500 hover:bg-indigo-200 hover:border-indigo-600 hover:text-indigo-600",
        disabled && "bg-indigo-100 border-indigo-200 text-indigo-300"
      ];
    }
    case "secondary-dark": {
      return [
        "bg-white text-teal-medium border-teal-medium hover:bg-teal-medium hover:text-white",
        disabled && "opacity-50"
      ];
    }
    case "tertiary": {
      return [
        "bg-off-white border-gray-600 text-gray-600",
        !disabled && "hover:text-gray-500 hover:border-gray-500"
      ];
    }
    case "warning": {
      return [
        "bg-white border-error text-error",
        !disabled &&
          "hover:bg-error-xlight hover:text-error-dark hover:border-error-dark"
      ];
    }
    case "warning-new": {
      return [
        "bg-hex-ffedee text-hex-ff4856 border-hex-ff4856",
        disabled && "opacity-50",
        !disabled && "hover:text-hex-b3323c hover:border-hex-b3323c"
      ];
    }
    case "secondary-gray": {
      return [
        "bg-white text-gray-400 border-gray-400 hover:text-gray-500 hover:border-gray-500",
        disabled && "opacity-50"
      ];
    }
    case "close-icon": {
      return [
        "bg-transparent border-0 cursor-pointer rounded-md -mt-2 -mr-2"
      ];
    }
  }
};

const getSizeClassName = (size: ButtonOrLinkProps["size"], hasIcon) => {
  switch (size) {
    case "normal": {
      return [
        "py-1.5 text-sm",
        hasIcon && "px-2 font-semibold",
        !hasIcon && "px-3.5 font-bold"
      ];
    }
    case "small": {
      return "p-2 text-xs font-semibold";
    }
    case "medium": {
      return "px-2.5 py-3 font-semibold text-sm !rounded-[5px]";
    }
    case "large": {
      return "px-5 py-2 font-bold text-base";
    }
  }
};

const getBaseClassName = (disabled: boolean, hasIcon) => {
  return [
    "cursor-pointer leading-none border border-solid rounded-sm",
    "font-sans inline-block transition box-border",
    "flex justify-center gap-1.5 items-center",
    hasIcon && "flex items-center",
    disabled && "!cursor-not-allowed"
  ];
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    kind = "tertiary",
    size = "normal",
    disabled,
    icon,
    children,
    ...props
  }: ButtonProps,
  ref
) {
  return (
    <button
      ref={ref}
      className={cx(
        getBaseClassName(disabled, !!icon),
        getKindClassName(kind, disabled),
        getSizeClassName(size, !!icon),
        className
      )}
      disabled={disabled}
      {...props}
    >
      {icon && <Icon name={icon} className="mr-2" size="small" />}
      {children}
    </button>
  );
});

export const ButtonLink = forwardRef<HTMLAnchorElement, LinkProps>(function ButtonLink(
  {
    className,
    kind = "tertiary",
    size = "normal",
    icon,
    disabled,
    to,
    children,
    ...props
  }: LinkProps,
  ref
) {
  return (
    <Link
      ref={ref}
      className={cx(
        getBaseClassName(disabled, !!icon),
        getKindClassName(kind, disabled),
        getSizeClassName(size, !!icon),
        disabled && "pointer-events-none",
        className
      )}
      to={disabled ? null : to}
      {...props}
    >
      {icon && <Icon name={icon} className="mr-2" />}
      {children}
    </Link>
  );
});

export const ButtonExternalLink = forwardRef<HTMLAnchorElement, ExternalLinkProps>(
  function ButtonLink(
    {
      className,
      kind = "tertiary",
      size = "normal",
      icon,
      disabled,
      href,
      children,
      ...props
    }: ExternalLinkProps,
    ref
  ) {
    return (
      <a
        className={cx(
          getBaseClassName(disabled, !!icon),
          getKindClassName(kind, disabled),
          getSizeClassName(size, !!icon),
          disabled && "pointer-events-none",
          className
        )}
        href={disabled ? null : href}
        target="_blank"
        rel="noopener noreferrer"
        {...props}
        ref={ref}
      >
        {icon && <Icon name={icon} className="mr-2" />}
        {children}
      </a>
    );
  }
);
